import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { lairAPI, missionsAPI } from '../api/lairApi';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import { fetchUser } from '../../../store/slices/userSlice';
import { fetchModules } from '../../inventory/store/inventorySlice';
import { fetchCharacters } from '../../inventory/store/characterSlice';
import { fetchNotifications } from '../../../store/slices/countersSlice';
import { serverOffsetMs } from '../utils/missionMath';
import i18n from '../../../i18n';

const errMsg = (error) =>
  error?.response?.data?.message || error?.response?.data?.error || error?.message
  || i18n.t('common.something_wrong', 'Something went wrong');

// ── Lair ────────────────────────────────────────────────────────────────────

export const fetchLair = createAsyncThunk(
  'lair/fetchLair',
  async (_, { rejectWithValue }) => {
    try {
      return await lairAPI.getLair();
    } catch (error) {
      return rejectWithValue(errMsg(error));
    }
  }
);

/**
 * Farm actions (v1.47.0) share one in-flight guard: unlock / assign / remove → LairStateDTO,
 * claim → { collected, state }. Coins move on unlock and claim; hero availability changes on assign / remove.
 */
const farmAction = (type, call, after) => createAsyncThunk(
  `lair/${type}`,
  async (arg, { rejectWithValue, dispatch }) => {
    try {
      const res = await call(arg);
      after(dispatch);
      return res;
    } catch (error) {
      return rejectWithValue(errMsg(error));
    }
  },
  { condition: (_, { getState }) => !getState().lair.lair.busyId }
);

export const unlockFarm = farmAction('unlockFarm', (buildingId) => lairAPI.unlockBuilding(buildingId),
  (dispatch) => dispatch(fetchWalletBalance()));
export const assignFarmHero = farmAction('assignFarmHero',
  ({ buildingId, characterId }) => lairAPI.assignHero(buildingId, characterId),
  (dispatch) => { dispatch(fetchMissions()); dispatch(fetchCharacters()); });
export const removeFarmHero = farmAction('removeFarmHero', (buildingId) => lairAPI.removeHero(buildingId),
  (dispatch) => { dispatch(fetchMissions()); dispatch(fetchCharacters()); });
export const claimFarm = farmAction('claimFarm', (buildingId) => lairAPI.claimFarm(buildingId),
  (dispatch) => dispatch(fetchWalletBalance()));

const farmBuildingIdOf = (action) => (typeof action.meta.arg === 'object' ? action.meta.arg.buildingId : action.meta.arg);

// ── Missions ────────────────────────────────────────────────────────────────

export const fetchMissions = createAsyncThunk(
  'lair/fetchMissions',
  async (_, { rejectWithValue }) => {
    try {
      return await missionsAPI.getMissions();
    } catch (error) {
      return rejectWithValue(errMsg(error));
    }
  }
);

export const startMission = createAsyncThunk(
  'lair/startMission',
  async ({ missionId, characterId }, { rejectWithValue, dispatch }) => {
    try {
      const run = await missionsAPI.startMission(missionId, characterId);
      dispatch(fetchMissions());
      // hero is now busy (onMission / missionEndsAt in GET /characters)
      dispatch(fetchCharacters());
      // expedition start counts towards the SEND_EXPEDITION task → Tasks badge
      dispatch(fetchNotifications());
      return run;
    } catch (error) {
      return rejectWithValue(errMsg(error));
    }
  },
  { condition: (_, { getState }) => !getState().lair.missions.startingMissionId }
);

export const claimMissionRun = createAsyncThunk(
  'lair/claimMissionRun',
  async (runId, { rejectWithValue, dispatch }) => {
    try {
      const run = await missionsAPI.claimMissionRun(runId);
      dispatch(fetchMissions());
      dispatch(fetchLair()); // DNA
      dispatch(fetchCharacters());
      const rewards = run?.rewards || [];
      if (rewards.some((r) => r.rewardType === 'COINS')) {
        dispatch(fetchWalletBalance());
      }
      if (rewards.some((r) => r.rewardType === 'CHARACTER_MODULE')) {
        dispatch(fetchUser());
        dispatch(fetchModules(null));
      }
      return run;
    } catch (error) {
      return rejectWithValue(errMsg(error));
    }
  },
  { condition: (_, { getState }) => !getState().lair.missions.claimingRunId }
);

// ── Slice ───────────────────────────────────────────────────────────────────

const initialState = {
  lair: {
    data: null,          // LairStateDTO
    fetchedAt: null,     // client ms when `data` arrived (for live income ticking)
    loading: false,
    loaded: false,
    error: null,
    offsetMs: 0,         // server now − client now (farm countdowns)
    busyId: null,        // buildingId of the in-flight farm action
    actionError: null,
    lastClaim: null,     // { buildingId, amount, at } → coin flash on the card
  },
  missions: {
    data: null,          // { serverTime, bonuses, missions, runs, characters }
    offsetMs: 0,         // server now − client now
    loading: false,
    loaded: false,
    error: null,
    startingMissionId: null,
    startError: null,
    claimingRunId: null,
    claimError: null,
    lastClaimedRun: null, // MissionRunDTO → result popup
  },
};

const setLairState = (lair, data) => {
  if (!data) return;
  lair.data = data;
  lair.fetchedAt = Date.now();
  lair.offsetMs = serverOffsetMs(data.serverTime, lair.fetchedAt);
  lair.loaded = true;
};

const lairSlice = createSlice({
  name: 'lair',
  initialState,
  reducers: {
    clearLastClaim(state) { state.lair.lastClaim = null; },
    clearLastClaimedRun(state) { state.missions.lastClaimedRun = null; },
    clearLairErrors(state) {
      state.lair.actionError = null;
      state.missions.startError = null;
      state.missions.claimError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLair
      .addCase(fetchLair.pending, (state) => {
        state.lair.loading = true;
        state.lair.error = null;
      })
      .addCase(fetchLair.fulfilled, (state, action) => {
        state.lair.loading = false;
        setLairState(state.lair, action.payload);
      })
      .addCase(fetchLair.rejected, (state, action) => {
        state.lair.loading = false;
        state.lair.error = action.payload || action.error.message;
      })

      // fetchMissions
      .addCase(fetchMissions.pending, (state) => {
        state.missions.loading = true;
        state.missions.error = null;
      })
      .addCase(fetchMissions.fulfilled, (state, action) => {
        state.missions.loading = false;
        state.missions.loaded = true;
        state.missions.data = action.payload;
        state.missions.offsetMs = serverOffsetMs(action.payload?.serverTime, Date.now());
      })
      .addCase(fetchMissions.rejected, (state, action) => {
        state.missions.loading = false;
        state.missions.error = action.payload || action.error.message;
      })

      // start
      .addCase(startMission.pending, (state, action) => {
        state.missions.startingMissionId = action.meta.arg.missionId;
        state.missions.startError = null;
      })
      .addCase(startMission.fulfilled, (state, action) => {
        state.missions.startingMissionId = null;
        // optimistic: show the run right away, the refetch replaces it
        const run = action.payload;
        if (run && state.missions.data) {
          const runs = state.missions.data.runs || [];
          if (!runs.some((r) => r.id === run.id)) state.missions.data.runs = [run, ...runs];
          const ch = (state.missions.data.characters || []).find((c) => c.id === run.characterId);
          if (ch) ch.onMission = true;
        }
      })
      .addCase(startMission.rejected, (state, action) => {
        state.missions.startingMissionId = null;
        state.missions.startError = action.payload || action.error.message;
      })

      // claim
      .addCase(claimMissionRun.pending, (state, action) => {
        state.missions.claimingRunId = action.meta.arg;
        state.missions.claimError = null;
      })
      .addCase(claimMissionRun.fulfilled, (state, action) => {
        state.missions.claimingRunId = null;
        const run = action.payload;
        state.missions.lastClaimedRun = run;
        if (run && state.missions.data?.runs) {
          state.missions.data.runs = state.missions.data.runs.map((r) => (r.id === run.id ? run : r));
        }
      })
      .addCase(claimMissionRun.rejected, (state, action) => {
        state.missions.claimingRunId = null;
        state.missions.claimError = action.payload || action.error.message;
      })

      // farm actions
      .addMatcher((a) => [unlockFarm, assignFarmHero, removeFarmHero, claimFarm].some((t) => t.pending.match(a)), (state, action) => {
        state.lair.busyId = farmBuildingIdOf(action);
        state.lair.actionError = null;
      })
      .addMatcher((a) => [unlockFarm, assignFarmHero, removeFarmHero].some((t) => t.fulfilled.match(a)), (state, action) => {
        state.lair.busyId = null;
        setLairState(state.lair, action.payload);
      })
      .addMatcher((a) => claimFarm.fulfilled.match(a), (state, action) => {
        state.lair.busyId = null;
        setLairState(state.lair, action.payload?.state);
        state.lair.lastClaim = { buildingId: action.meta.arg, amount: Number(action.payload?.collected) || 0, at: Date.now() };
      })
      .addMatcher((a) => [unlockFarm, assignFarmHero, removeFarmHero, claimFarm].some((t) => t.rejected.match(a)), (state, action) => {
        state.lair.busyId = null;
        state.lair.actionError = action.payload || action.error.message;
      });
  },
});

export const {
  clearLastClaim, clearLastClaimedRun, clearLairErrors,
} = lairSlice.actions;

export const selectLair = (state) => state.lair.lair;
export const selectMissions = (state) => state.lair.missions;

export default lairSlice.reducer;
